import { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import api from "@/lib/api";
import { Facebook, Instagram, Globe, Chrome, MapPin, User as UserIcon, Filter, AlertTriangle, CheckCircle2 } from "lucide-react";

const EVENT_LABELS = {
  AddToCart: "Aggiunto al carrello",
  ViewContent: "Visto prodotto",
  InitiateCheckout: "Check-out avviato",
  Search: "Ricerca",
  AddToWishlist: "Aggiunto a preferiti",
  Purchase: "Acquisto",
  Lead: "Lead",
};

const SOURCE_META = {
  facebook: { label: "Facebook", color: "bg-[#1877F2]", Icon: Facebook },
  instagram: { label: "Instagram", color: "bg-[#E1306C]", Icon: Instagram },
  google: { label: "Google", color: "bg-[#EA4335]", Icon: Chrome },
  direct: { label: "Diretto", color: "bg-slate-600", Icon: Globe },
  other: { label: "Altro", color: "bg-slate-500", Icon: Globe },
  unknown: { label: "Ignoto", color: "bg-slate-400", Icon: Globe },
};

export default function AdminTracking() {
  const [items, setItems] = useState([]);
  const [bySource, setBySource] = useState([]);
  const [abandonedTotal, setAbandonedTotal] = useState(0);
  const [filterEvent, setFilterEvent] = useState("AddToCart");
  const [filterSource, setFilterSource] = useState("");
  const [abandonedOnly, setAbandonedOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filterEvent) params.event_name = filterEvent;
      if (filterSource) params.source = filterSource;
      if (abandonedOnly) params.abandoned_only = true;
      const r = await api.get("/admin/tracking-events", { params });
      setItems(r.data.items || []);
      setBySource(r.data.by_source || []);
      setAbandonedTotal(r.data.abandoned_total || 0);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filterEvent, filterSource, abandonedOnly]); // eslint-disable-line

  const fbCount = useMemo(() => bySource.find((s) => s.source === "facebook")?.count || 0, [bySource]);
  const fbValue = useMemo(() => bySource.find((s) => s.source === "facebook")?.value || 0, [bySource]);

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">Attribuzione traffico</div>
        <h1 className="font-admin text-2xl font-bold">Tracking eventi</h1>
        <p className="text-xs text-xd-muted mt-1">Chi aggiunge al carrello e da quale canale (Facebook, Google, diretto…)</p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-xd-border p-4">
          <div className="text-xs text-xd-muted">Da Facebook</div>
          <div className="font-display text-2xl font-semibold text-[#1877F2] mt-1" data-testid="tile-fb-count">{fbCount}</div>
          <div className="text-[11px] text-xd-muted">€ {fbValue.toFixed(2)} valore</div>
        </div>
        {["instagram", "google", "direct"].map((s) => {
          const row = bySource.find((r) => r.source === s);
          const meta = SOURCE_META[s];
          return (
            <div key={s} className="bg-white rounded-xl border border-xd-border p-4">
              <div className="text-xs text-xd-muted">Da {meta.label}</div>
              <div className={`font-display text-2xl font-semibold mt-1 ${s === "google" ? "text-[#EA4335]" : s === "instagram" ? "text-[#E1306C]" : "text-slate-700"}`}>{row?.count || 0}</div>
              <div className="text-[11px] text-xd-muted">€ {(row?.value || 0).toFixed(2)} valore</div>
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => { setFilterEvent("AddToCart"); setAbandonedOnly(true); }}
          data-testid="tile-abandoned"
          className={`text-left rounded-xl border p-4 transition-colors ${abandonedOnly ? "border-amber-400 bg-amber-50" : "bg-white border-xd-border hover:border-amber-300"}`}
        >
          <div className="text-xs text-xd-muted inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Carrelli abbandonati</div>
          <div className="font-display text-2xl font-semibold text-amber-600 mt-1" data-testid="tile-abandoned-count">{abandonedTotal}</div>
          <div className="text-[11px] text-xd-muted">Nessun ordine entro 24h</div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-xd-border p-3 mb-4 flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-xd-muted shrink-0" />
        <select value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)} data-testid="filter-event" className="border border-xd-border rounded-md px-3 py-2 text-sm bg-white">
          {Object.entries(EVENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          <option value="">Tutti gli eventi</option>
        </select>
        <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} data-testid="filter-source" className="border border-xd-border rounded-md px-3 py-2 text-sm bg-white">
          <option value="">Tutte le sorgenti</option>
          {Object.entries(SOURCE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 text-xs font-medium cursor-pointer select-none" data-testid="toggle-abandoned-wrap">
          <input type="checkbox" checked={abandonedOnly} onChange={(e) => setAbandonedOnly(e.target.checked)} data-testid="toggle-abandoned" className="w-4 h-4 accent-amber-500" />
          <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Solo carrelli abbandonati</span>
        </label>
        <div className="ml-auto text-xs text-xd-muted">{items.length} eventi</div>
      </div>

      {/* Events list */}
      <div className="bg-white rounded-xl border border-xd-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-slate-50 border-b border-xd-border">
              <tr className="text-left text-xs uppercase text-xd-muted">
                <th className="p-3">Quando</th>
                <th className="p-3">Evento</th>
                <th className="p-3">Sorgente</th>
                <th className="p-3">Prodotto</th>
                <th className="p-3">Cliente</th>
                <th className="p-3">Valore</th>
                <th className="p-3">Esito</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-6 text-center text-xd-muted">Caricamento…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-xd-muted">Nessun evento trovato con questi filtri.</td></tr>}
              {items.map((e) => {
                const meta = SOURCE_META[e.source] || SOURCE_META.unknown;
                const isAddToCart = e.event_name === "AddToCart";
                return (
                  <tr key={e.id} data-testid={`event-row-${e.id}`} className="border-b border-xd-border last:border-0 hover:bg-slate-50/60">
                    <td className="p-3 whitespace-nowrap text-xs">{new Date(e.created_at).toLocaleString("it-IT")}</td>
                    <td className="p-3"><span className="inline-flex items-center gap-1 text-xs font-medium">{EVENT_LABELS[e.event_name] || e.event_name}</span></td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold text-white rounded-full px-2 py-0.5 ${meta.color}`}>
                        <meta.Icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="p-3 max-w-[240px] truncate" title={e.product_name}>{e.product_name || "—"}</td>
                    <td className="p-3">
                      {e.customer_name || e.email || e.phone ? (
                        <div className="flex flex-col">
                          <span className="font-medium text-xs inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /> {e.customer_name || "Ospite"}</span>
                          {e.email && <span className="text-[10px] text-xd-muted truncate max-w-[180px]">{e.email}</span>}
                        </div>
                      ) : (
                        <span className="text-xs text-xd-muted inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Anonimo {e.ip ? `(${e.ip})` : ""}</span>
                      )}
                    </td>
                    <td className="p-3 font-mono-tech text-xs">{e.value ? `€ ${Number(e.value).toFixed(2)}` : "—"}</td>
                    <td className="p-3">
                      {isAddToCart ? (
                        e.converted ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="badge-converted">
                            <CheckCircle2 className="w-3 h-3" /> Ordine completato
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="badge-abandoned">
                            <AlertTriangle className="w-3 h-3" /> Abbandonato
                          </span>
                        )
                      ) : (
                        <span className="text-[10px] text-xd-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
